import { useState } from "react";
import { AppLayout } from "@project/components/AppLayout";
import {
  mockInterviews,
  mockCandidates,
  type Interview,
  type InterviewRound,
} from "@project/data/mockData";
import { Input } from "@project/components/ui/input";
import { Button } from "@project/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import { Textarea } from "@project/components/ui/textarea";
import { Label } from "@project/components/ui/label";
import {
  Search,
  Calendar,
  Clock,
  Plus,
  Sparkles,
  Loader2,
  MessageSquare,
  Star,
  Brain,
  FileText,
  CheckCircle2,
} from "lucide-react";
import { motion } from "framer-motion";
import {
  staggerContainer as container,
  fadeInUp as item,
} from "@project/lib/animations";
import { toast } from "@project/hooks/use-toast";
import { useAIAction } from "@project/hooks/useAIAction";
import AIResultDisplay from "@project/components/AIResultDisplay";

const statusColors: Record<string, string> = {
  Scheduled:
    "text-[hsl(var(--status-posted))] bg-[hsl(var(--status-posted-bg))]",
  Completed:
    "text-[hsl(var(--status-active))] bg-[hsl(var(--status-active-bg))]",
  Cancelled: "text-destructive bg-[hsl(var(--status-filled-bg))]",
  "No Show":
    "text-[hsl(var(--status-closed))] bg-[hsl(var(--status-closed-bg))]",
};

const InterviewsPage = () => {
  const [interviews, setInterviews] = useState(mockInterviews);
  const [activeTab, setActiveTab] = useState("All");
  const [search, setSearch] = useState("");
  const [selectedInterview, setSelectedInterview] = useState<Interview | null>(
    null
  );
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [aiQuestionsOpen, setAiQuestionsOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiQuestions, setAiQuestions] = useState<string[]>([]);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [fbRating, setFbRating] = useState(0);
  const [fbStrengths, setFbStrengths] = useState("");
  const [fbWeaknesses, setFbWeaknesses] = useState("");
  const [fbNotes, setFbNotes] = useState("");
  const [fbRecommendation, setFbRecommendation] = useState<
    "Hire" | "Reject" | "Next Round" | "Hold"
  >("Next Round");
  const [schCandidate, setSchCandidate] = useState("");
  const [schRound, setSchRound] = useState<InterviewRound>("Round 1");
  const [schDate, setSchDate] = useState("");
  const [schTime, setSchTime] = useState("");
  const [schInterviewer, setSchInterviewer] = useState("");
  const [aiSummaryOpen, setAiSummaryOpen] = useState(false);
  const aiSummary = useAIAction();
  const tabFilters = [
    { label: "All", count: interviews.length },
    {
      label: "Scheduled",
      count: interviews.filter(i => i.status === "Scheduled").length,
    },
    {
      label: "Completed",
      count: interviews.filter(i => i.status === "Completed").length,
    },
  ];

  const filtered = (
    activeTab === "All"
      ? interviews
      : interviews.filter(i => i.status === activeTab)
  ).filter(
    i =>
      !search ||
      i.candidateName.toLowerCase().includes(search.toLowerCase()) ||
      i.jobTitle.toLowerCase().includes(search.toLowerCase())
  );

  const handleGenerateQuestions = async (interview: Interview) => {
    toast({
      title: "Feature Disabled",
      description:
        "AI Interview Question generation is currently unavailable because Supabase has been removed.",
      variant: "destructive",
    });
  };

  const handleSubmitFeedback = () => {
    if (!selectedInterview) {
      return;
    }
    setInterviews(prev =>
      prev.map(i =>
        i.id !== selectedInterview.id
          ? i
          : {
              ...i,
              status: "Completed" as const,
              feedback: {
                rating: fbRating,
                strengths: fbStrengths
                  .split(",")
                  .map(s => s.trim())
                  .filter(Boolean),
                weaknesses: fbWeaknesses
                  .split(",")
                  .map(s => s.trim())
                  .filter(Boolean),
                notes: fbNotes,
                recommendation: fbRecommendation,
              },
            }
      )
    );
    setFeedbackOpen(false);
    toast({ title: "Feedback Submitted" });
  };

  const handleSchedule = () => {
    if (!schCandidate || !schDate || !schTime) {
      toast({ title: "Fill required fields", variant: "destructive" });
      return;
    }
    const candidate = mockCandidates.find(c => c.id === schCandidate);
    setInterviews(prev => [
      {
        id: `i${Date.now()}`,
        candidateId: schCandidate,
        candidateName: candidate?.name || "Unknown",
        candidateAvatar: candidate?.avatar || "??",
        jobId: candidate?.jobId || "",
        jobTitle: candidate?.jobTitle || "",
        round: schRound,
        status: "Scheduled" as const,
        interviewerId: "u1",
        interviewerName: schInterviewer || "Admirra John",
        date: schDate,
        time: schTime,
        duration: "45 min",
      },
      ...prev,
    ]);
    setScheduleOpen(false);
    toast({ title: "Interview Scheduled" });
  };

  return (
    <AppLayout>
      <motion.div
        className="space-y-4"
        variants={container}
        initial="hidden"
        animate="show"
      >
        <motion.div
          variants={item}
          className="flex items-center justify-between"
        >
          <div>
            <h1 className="font-display text-lg font-bold tracking-tight text-foreground">
              Interviews
            </h1>
            <p className="mt-0.5 hidden text-[12px] text-muted-foreground sm:block">
              Schedule, track, and manage interview rounds
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              className="ai-btn h-8 gap-1.5 rounded-lg text-[12px] font-semibold"
              onClick={async () => {
                const completed = interviews.filter(i => i.feedback);
                if (completed.length === 0) {
                  toast({
                    title: "No feedback data",
                    description: "Complete some interviews first.",
                    variant: "destructive",
                  });
                  return;
                }
                setAiSummaryOpen(true);
                await aiSummary.run(`Generate a comprehensive interview summary report based on all feedback data:

${completed.map(i => `- **${i.candidateName}** (${i.jobTitle}, ${i.round}): Rating=${i.feedback!.rating}/5, Strengths=${i.feedback!.strengths.join(", ")}, Weaknesses=${i.feedback!.weaknesses.join(", ")}, Recommendation=${i.feedback!.recommendation}, Notes=${i.feedback!.notes}`).join("\n")}

Provide:
1. **Overall Summary** - aggregate view of all interviews
2. **Top Performers** - candidates with best feedback
3. **Concerns** - candidates with issues
4. **Hiring Recommendations** - final AI recommendation for each candidate
5. **Next Steps** - suggested actions for each candidate`);
              }}
            >
              <Brain className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">AI Summary</span>
            </Button>
            <Button
              size="sm"
              onClick={() => setScheduleOpen(true)}
              className="btn-glow h-8 gap-1.5 rounded-lg text-[12px] font-semibold"
            >
              <Plus className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Schedule Interview</span>
              <span className="sm:hidden">Schedule</span>
            </Button>
          </div>
        </motion.div>

        <motion.div
          variants={item}
          className="no-scrollbar flex items-center gap-0.5 overflow-x-auto rounded-xl bg-muted/50 p-0.5"
        >
          {tabFilters.map(tab => (
            <button
              key={tab.label}
              onClick={() => setActiveTab(tab.label)}
              className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-1.5 text-[12px] font-medium transition-all ${activeTab === tab.label ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
            >
              {tab.label}
              <span
                className={`rounded-md px-1.5 py-0 text-[10px] font-bold ${activeTab === tab.label ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}
              >
                {tab.count}
              </span>
            </button>
          ))}
        </motion.div>

        <motion.div variants={item}>
          <div className="relative max-w-xs">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="h-8 rounded-lg border-border/60 bg-card pl-8 text-[12px]"
            />
          </div>
        </motion.div>

        <motion.div variants={item} className="space-y-2">
          {filtered.length === 0 ? (
            <div className="section-card p-8 text-center">
              <Calendar className="mx-auto mb-2 h-8 w-8 text-muted-foreground/40" />
              <p className="text-[13px] text-muted-foreground">
                No interviews found
              </p>
            </div>
          ) : (
            filtered.map(interview => (
              <div key={interview.id} className="section-card p-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary-glow">
                    <span className="text-[11px] font-bold text-white">
                      {interview.candidateAvatar}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-[13px] font-semibold text-foreground">
                        {interview.candidateName}
                      </p>
                      <span
                        className={`rounded-lg px-2 py-0.5 text-[10px] font-semibold ${statusColors[interview.status]}`}
                      >
                        {interview.status}
                      </span>
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      {interview.jobTitle} · {interview.round}
                    </p>
                  </div>
                  <div className="hidden shrink-0 text-right sm:block">
                    <p className="flex items-center gap-1 text-[12px] font-semibold text-foreground">
                      <Calendar className="h-3 w-3" />
                      {interview.date}
                    </p>
                    <p className="flex items-center gap-1 text-[11px] text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {interview.time}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      onClick={() => {
                        setSelectedInterview(interview);
                        handleGenerateQuestions(interview);
                      }}
                      className="ai-btn-outline rounded-lg p-2 transition-colors"
                      title="AI Questions"
                    >
                      <Sparkles className="h-4 w-4" />
                    </button>
                    {interview.status === "Scheduled" && (
                      <button
                        onClick={() => {
                          setSelectedInterview(interview);
                          setFeedbackOpen(true);
                        }}
                        className="rounded-lg p-2 transition-colors hover:bg-muted"
                        title="Feedback"
                      >
                        <MessageSquare className="h-4 w-4 text-muted-foreground" />
                      </button>
                    )}
                  </div>
                </div>
                {interview.feedback && (
                  <div className="mt-3 flex items-center gap-4 border-t border-border/30 pt-3 text-[11px]">
                    <div className="flex items-center gap-1">
                      <Star className="h-3 w-3 fill-warning text-warning" />
                      <span className="font-bold text-foreground">
                        {interview.feedback.rating}/5
                      </span>
                    </div>
                    <span
                      className={`font-semibold ${interview.feedback.recommendation === "Hire" ? "text-[hsl(var(--status-active))]" : interview.feedback.recommendation === "Reject" ? "text-destructive" : "text-[hsl(var(--status-onhold))]"}`}
                    >
                      {interview.feedback.recommendation}
                    </span>
                    <span className="text-muted-foreground">
                      by {interview.interviewerName}
                    </span>
                  </div>
                )}
              </div>
            ))
          )}
        </motion.div>
      </motion.div>

      <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">
              Schedule Interview
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label className="text-[12px]">Candidate *</Label>
              <Select value={schCandidate} onValueChange={setSchCandidate}>
                <SelectTrigger>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {mockCandidates
                    .filter(c =>
                      ["Shortlisted", "Interview", "Screened"].includes(c.stage)
                    )
                    .map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name} — {c.jobTitle}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[12px]">Round</Label>
                <Select
                  value={schRound}
                  onValueChange={v => setSchRound(v as InterviewRound)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(
                      [
                        "Round 1",
                        "Round 2",
                        "Technical",
                        "HR",
                        "Final",
                      ] as InterviewRound[]
                    ).map(r => (
                      <SelectItem key={r} value={r}>
                        {r}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-[12px]">Interviewer</Label>
                <Input
                  value={schInterviewer}
                  onChange={e => setSchInterviewer(e.target.value)}
                  placeholder="Name"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[12px]">Date *</Label>
                <Input
                  type="date"
                  value={schDate}
                  onChange={e => setSchDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[12px]">Time *</Label>
                <Input
                  type="time"
                  value={schTime}
                  onChange={e => setSchTime(e.target.value)}
                />
              </div>
            </div>
            <Button onClick={handleSchedule} className="btn-glow w-full">
              Schedule
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={aiQuestionsOpen} onOpenChange={setAiQuestionsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <Sparkles className="h-4 w-4 text-primary" />
              AI Interview Questions
            </DialogTitle>
          </DialogHeader>
          {aiLoading ? (
            <div className="flex flex-col items-center gap-3 py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-[13px] text-muted-foreground">Generating...</p>
            </div>
          ) : (
            <div className="space-y-3 pt-2">
              {aiQuestions.map((q, i) => (
                <div key={i} className="flex gap-3 rounded-xl bg-muted/30 p-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-[11px] font-bold text-primary">
                    {i + 1}
                  </span>
                  <p className="text-[12px] leading-relaxed text-foreground">
                    {q}
                  </p>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={feedbackOpen} onOpenChange={setFeedbackOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">
              Interview Feedback
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label className="text-[12px]">Rating</Label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map(n => (
                  <button
                    key={n}
                    onClick={() => setFbRating(n)}
                    className={`h-8 w-8 rounded-lg text-[13px] font-bold transition-all ${fbRating >= n ? "bg-warning text-white" : "bg-muted text-muted-foreground"}`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-[12px]">Strengths</Label>
              <Input
                value={fbStrengths}
                onChange={e => setFbStrengths(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[12px]">Weaknesses</Label>
              <Input
                value={fbWeaknesses}
                onChange={e => setFbWeaknesses(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[12px]">Notes</Label>
              <Textarea
                value={fbNotes}
                onChange={e => setFbNotes(e.target.value)}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[12px]">Recommendation</Label>
              <Select
                value={fbRecommendation}
                onValueChange={v => setFbRecommendation(v as any)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Hire">✅ Hire</SelectItem>
                  <SelectItem value="Next Round">🔄 Next Round</SelectItem>
                  <SelectItem value="Hold">⏸ Hold</SelectItem>
                  <SelectItem value="Reject">❌ Reject</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleSubmitFeedback} className="btn-glow w-full">
              Submit Feedback
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Interview Summary Dialog */}
      <Dialog open={aiSummaryOpen} onOpenChange={setAiSummaryOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <Brain className="h-5 w-5 text-primary" />
              AI Interview Summary Report
            </DialogTitle>
          </DialogHeader>
          {aiSummary.loading ? (
            <div className="flex flex-col items-center gap-3 py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-[13px] text-muted-foreground">
                Analyzing all interview feedback...
              </p>
            </div>
          ) : (
            <AIResultDisplay content={aiSummary.result} />
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
};

export default InterviewsPage;
